import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserEdit,
  faSave,
  faUndo,
  faIdCard,
  faUserCircle,
  faGraduationCap,
  faHome,
  faPhone,
  faUser,
  faVenusMars,
  faBuilding,
  faSync,
  faArrowLeft,
  faCheckCircle,
  faInfoCircle,
  faSpinner,
  faExclamationTriangle,
  faWarning,
  faLock,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import "./editStudentPanel.css";

// API base URL - same as your FetchStudents component
const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const EditStudentPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [backendError, setBackendError] = useState(null);
  const [originalStudentId, setOriginalStudentId] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [originalData, setOriginalData] = useState({});

  // Get student data from navigation state
  const studentData = location.state?.studentData || location.state?.student;

  const [formData, setFormData] = useState({
    name: "",
    father_name: "",
    mobile: "",
    b_form_no: "",
    address: "",
    gender: "",
    class: "",
    section: "",
    student_id: "",
    family_status: "Active",
  });

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const genders = ["Male", "Female"];
  const familyStatuses = ["Family", "Solo"];

  // Check if student belongs to a family
  const isFamilyStudent = formData.family_status === "Family";

  // Function to auto-format mobile number
  const formatMobileNumber = (input) => {
    if (!input) return "";

    // Remove all non-digits
    let digits = input.replace(/\D/g, "");

    // If it starts with 0 and has 11 digits, format as 03XX-XXXXXXX
    if (digits.length === 11 && digits.startsWith("0")) {
      return digits.replace(/(\d{4})(\d{7})/, "$1-$2");
    }
    // If it starts with 3 and has 10 digits (without leading 0), add 0 and format
    else if (digits.length === 10 && digits.startsWith("3")) {
      digits = "0" + digits;
      return digits.replace(/(\d{4})(\d{7})/, "$1-$2");
    }
    // If it's 4 digits or more but less than full, add dash at position 4
    else if (digits.length >= 4 && digits.length < 11) {
      const part1 = digits.substring(0, 4);
      const part2 = digits.substring(4);
      return part1 + (part2 ? "-" + part2 : "-");
    }
    // For any other case, return as is
    return input;
  };

  // Function to auto-format B-Form/CNIC
  const formatBFormNumber = (input) => {
    if (!input) return "";

    // Remove all non-digits
    let digits = input.replace(/\D/g, "");

    // Format as 35202-1234567-1 (13 digits total)
    if (digits.length === 13) {
      return digits.replace(/(\d{5})(\d{7})(\d{1})/, "$1-$2-$3");
    }
    // Partial formatting as user types
    else if (digits.length > 5 && digits.length <= 12) {
      const part1 = digits.substring(0, 5);
      const part2 = digits.substring(5, 12);
      const part3 = digits.substring(12);
      return part1 + (part2 ? "-" + part2 : "-") + (part3 ? "-" + part3 : "");
    } else if (digits.length === 5) {
      return digits + "-";
    }

    return input;
  };

  // Fetch student data on component mount
  useEffect(() => {
    const fetchStudentData = async () => {
      setIsLoading(true);
      setBackendError(null);
      setFormErrors({});

      try {
        let data;

        if (studentData && typeof studentData === "object") {
          // Use the data passed from navigation
          data = studentData;

          // Store original student ID
          if (data.student_id) {
            setOriginalStudentId(data.student_id);
          }
        } else if (studentData) {
          // Fetch by student ID if only ID was passed
          try {
            const response = await axios.get(
              `${API_BASE_URL}/studentCrudOP.php?action=get-student-details&id=${encodeURIComponent(
                studentData
              )}`,
              {
                timeout: 10000,
              }
            );

            if (response.data && response.data.success) {
              data = response.data.data;
              setOriginalStudentId(data.student_id);
            } else {
              throw new Error(
                response.data?.error || "Failed to fetch student data"
              );
            }
          } catch (fetchError) {
            console.error("API fetch error:", fetchError);
            throw new Error(`PHP backend error: ${fetchError.message}`);
          }
        } else {
          throw new Error("No student data provided");
        }

        console.log("Fetched Student Data:", data);

        // Store original data for comparison and reset functionality
        setOriginalData(data);

        // Format mobile number if it exists
        const formattedMobile = data.mobile
          ? formatMobileNumber(data.mobile)
          : "";
        const formattedBForm = data.b_form_no
          ? formatBFormNumber(data.b_form_no)
          : "";

        // Update form data with formatted values
        const updatedFormData = {
          name: data.name || "",
          father_name: data.father_name || "",
          mobile: formattedMobile,
          b_form_no: formattedBForm,
          address: data.address || "",
          gender: data.gender || "",
          class: data.class || "",
          section: data.section || "",
          student_id: data.student_id || "",
          family_status: data.family_status || "Active",
        };

        setFormData(updatedFormData);

        // Fetch sections for the class
        if (data.class) {
          await fetchSections(data.class);
        }
      } catch (error) {
        console.error("Failed to fetch student data:", error);
        setBackendError(error.message);

        Swal.fire({
          icon: "error",
          title: "Error Loading Data",
          html: `
            <div style="text-align: left;">
              <p><strong>${error.message}</strong></p>
              <p>Please check:</p>
              <ul>
                <li>PHP server is running</li>
                <li>Student ID is correct</li>
                <li>Database connection is working</li>
              </ul>
            </div>
          `,
          background: "#f8fafc",
          color: "#2d3748",
          confirmButtonColor: "#667eea",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (studentData) {
      fetchStudentData();
    } else {
      Swal.fire({
        icon: "error",
        title: "No Student Selected",
        text: "Please select a student to edit first.",
        confirmButtonColor: "#667eea",
      }).then(() => navigate("/Edit-Students-Data"));
    }
  }, [studentData, navigate]);

  // Fetch sections based on class
  const fetchSections = async (tClass) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/studentCrudOP.php?action=get-sections&class=${encodeURIComponent(
          tClass
        )}`,
        {
          timeout: 5000,
        }
      );

      if (response.data && response.data.success) {
        setSections(response.data.sections || []);
      } else {
        setSections([]);
        console.warn("No sections found for class:", tClass);
      }
    } catch (error) {
      console.error("Failed to fetch sections:", error);
      setSections([]);

      // Provide default sections if API fails
      const defaultSections = ["A", "B", "C", "D", "E"];
      setSections(defaultSections);
    }
  };

  // Handle input changes with auto-formatting
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      let formattedValue = value;

      // Auto-format mobile number
      if (name === "mobile") {
        formattedValue = formatMobileNumber(value);
      }

      // Auto-format B-Form number
      if (name === "b_form_no") {
        formattedValue = formatBFormNumber(value);
      }

      setFormData((prev) => ({ ...prev, [name]: formattedValue }));

      // Clear error for this field when user types
      if (formErrors[name]) {
        setFormErrors((prev) => ({ ...prev, [name]: null }));
      }

      if (name === "class" && value) {
        fetchSections(value);
      }
    },
    [formErrors]
  );

  // Handle mobile number blur (final formatting)
  const handleMobileBlur = useCallback(
    (e) => {
      const { name, value } = e.target;
      const formattedValue = formatMobileNumber(value);

      if (formattedValue !== value) {
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      }

      // Validate format on blur
      if (formattedValue && !/^03\d{2}-\d{7}$/.test(formattedValue)) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: "Mobile must be in format: 03XX-XXXXXXX",
        }));
      } else if (formErrors[name]) {
        // Clear error if format is correct
        setFormErrors((prev) => ({ ...prev, [name]: null }));
      }
    },
    [formErrors]
  );

  // Handle B-Form blur (final formatting)
  const handleBFormBlur = useCallback(
    (e) => {
      const { name, value } = e.target;
      const formattedValue = formatBFormNumber(value);

      if (formattedValue !== value) {
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      }

      // Validate format on blur (only if field has value)
      if (formattedValue && !/^\d{5}-\d{7}-\d{1}$/.test(formattedValue)) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: "B-Form must be in format: 35202-1234567-1",
        }));
      } else if (formErrors[name]) {
        // Clear error if format is correct
        setFormErrors((prev) => ({ ...prev, [name]: null }));
      }
    },
    [formErrors]
  );

  // Form validation
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Required fields (always required)
    if (!formData.name.trim()) {
      errors.name = "Student name is required";
      isValid = false;
    }

    if (!formData.father_name.trim()) {
      errors.father_name = "Father's name is required";
      isValid = false;
    }

    if (!formData.mobile.trim()) {
      errors.mobile = "Mobile number is required";
      isValid = false;
    }

    if (!formData.gender) {
      errors.gender = "Gender is required";
      isValid = false;
    }

    if (!formData.class) {
      errors.class = "Class is required";
      isValid = false;
    }

    if (!formData.section) {
      errors.section = "Section is required";
      isValid = false;
    }

    if (!formData.address.trim()) {
      errors.address = "Address is required";
      isValid = false;
    }

    // Format validation (always check format)
    if (formData.mobile && !/^03\d{2}-\d{7}$/.test(formData.mobile)) {
      errors.mobile = "Mobile must be in format: 03XX-XXXXXXX";
      isValid = false;
    }

    if (formData.b_form_no && !/^\d{5}-\d{7}-\d{1}$/.test(formData.b_form_no)) {
      errors.b_form_no = "B-Form must be in format: 35202-1234567-1";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBackendError(null);
    setFormErrors({});

    // Validate form
    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix the errors in the form before submitting.",
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    // For family students, check if shared fields have been modified
    if (isFamilyStudent) {
      const sharedFieldsModified = [
        "father_name",
        "mobile",
        "b_form_no",
        "address",
      ].some((field) => formData[field] !== originalData[field]);

      if (sharedFieldsModified) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Update Shared Fields",
          html: `
            <div style="text-align: left;">
              <p><strong>This student belongs to a family.</strong></p>
              <p>You cannot modify shared family information:</p>
              <ul>
                <li>Father's Name</li>
                <li>Mobile Number</li>
                <li>B-Form/CNIC</li>
                <li>Address</li>
              </ul>
              <p>Only individual student information can be updated.</p>
            </div>
          `,
          background: "#f8fafc",
          color: "#2d3748",
          confirmButtonColor: "#667eea",
        });
        return;
      }
    }

    setIsSaving(true);

    try {
      // Prepare form data for PHP backend
      const requestData = {
        action: "update-student",
        originalStudentId: originalStudentId,
        student_id: formData.student_id || originalStudentId,
        name: formData.name,
        father_name: formData.father_name,
        mobile: formData.mobile,
        b_form_no: formData.b_form_no || "",
        address: formData.address,
        gender: formData.gender,
        class: formData.class,
        section: formData.section,
        family_status: formData.family_status,
      };

      console.log("Sending update data:", requestData);

      // Send update request using FormData for proper POST handling
      const formDataToSend = new FormData();
      Object.keys(requestData).forEach((key) => {
        formDataToSend.append(key, requestData[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}/studentCrudOP.php`,
        formDataToSend,
        {
          timeout: 10000,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Update response:", response.data);

      if (response.data && response.data.success) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message || "Student updated successfully",
          showConfirmButton: false,
          timer: 1500,
          background: "#ffffff",
        });

        // Update original student ID if it was changed
        if (response.data.student_id) {
          setOriginalStudentId(response.data.student_id);
        }

        navigate("/Edit-Students-Data");
      } else {
        throw new Error(response.data?.error || "Update failed");
      }
    } catch (error) {
      console.error("Error updating student data:", error);

      let errorMessage = "Failed to update student. Please try again.";
      let errorDetails = "";

      // Extract error details from response
      if (error.response?.data) {
        console.error("Error response data:", error.response.data);

        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }

        if (error.response.data.details) {
          errorDetails = error.response.data.details;
        }

        // Handle specific error cases
        if (errorMessage.includes("Student ID already exists")) {
          setFormErrors((prev) => ({
            ...prev,
            student_id: "This Student ID is already taken",
          }));
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setBackendError(errorMessage);

      // Show error dialog with details
      let errorHtml = `<div style="text-align: left;"><p><strong>${errorMessage}</strong></p>`;

      if (errorDetails) {
        errorHtml += `<p>${errorDetails}</p>`;
      }

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        html: errorHtml,
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Generate new student ID
  const handleGenerateStudentID = async () => {
    const { class: tClass, section } = formData;

    if (!tClass || !section) {
      Swal.fire({
        icon: "warning",
        title: "Required Information",
        text: "Please select both class and section before generating a student ID.",
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    try {
      const year = new Date().getFullYear();
      const requestData = {
        action: "generate-student-id",
        className: tClass,
        section: section,
        year: year,
      };

      console.log("Generating ID with:", requestData);

      const response = await axios.post(
        `${API_BASE_URL}/studentCrudOP.php`,
        requestData,
        {
          timeout: 5000,
          headers: {
            "Content-Type": "application/json",
          },
          transformRequest: [
            (data) => {
              // Convert object to URL-encoded string
              const formData = new URLSearchParams();
              for (const key in data) {
                formData.append(key, data[key]);
              }
              return formData;
            },
          ],
        }
      );

      console.log("Generate ID response:", response.data);

      if (response.data && response.data.success) {
        const newStudentId = response.data.nextId;

        setFormData((prev) => ({ ...prev, student_id: newStudentId }));

        // Clear any previous student_id error
        if (formErrors.student_id) {
          setFormErrors((prev) => ({ ...prev, student_id: null }));
        }

        Swal.fire({
          icon: "success",
          title: "ID Generated",
          text: `New Student ID: ${newStudentId}`,
          background: "#f8fafc",
          color: "#2d3748",
          confirmButtonColor: "#667eea",
          timer: 2000,
        });
      } else {
        throw new Error(response.data?.error || "Failed to generate ID");
      }
    } catch (error) {
      console.error("Error generating student ID:", error);

      let errorMessage = "Failed to generate student ID.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      Swal.fire({
        icon: "error",
        title: "Generation Failed",
        text: errorMessage,
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
      });
    }
  };

  // Reset form to original values
  const handleReset = () => {
    if (originalData) {
      // Reload original data with formatted values
      const formattedMobile = originalData.mobile
        ? formatMobileNumber(originalData.mobile)
        : "";
      const formattedBForm = originalData.b_form_no
        ? formatBFormNumber(originalData.b_form_no)
        : "";

      setFormData({
        name: originalData.name || "",
        father_name: originalData.father_name || "",
        mobile: formattedMobile,
        b_form_no: formattedBForm,
        address: originalData.address || "",
        gender: originalData.gender || "",
        class: originalData.class || "",
        section: originalData.section || "",
        student_id: originalData.student_id || "",
        family_status: originalData.family_status || "Active",
      });

      if (originalData.class) {
        fetchSections(originalData.class);
      }

      // Clear all errors
      setFormErrors({});
      setBackendError(null);

      Swal.fire({
        icon: "info",
        title: "Form Reset",
        text: "All changes have been reverted to original values.",
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
        timer: 1500,
      });
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/Edit-Students-Data");
  };

  // Test PHP backend connection
  const testBackendConnection = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/studentCrudOP.php?action=get-student-details&id=${encodeURIComponent(
          originalStudentId
        )}`,
        { timeout: 5000 }
      );
      return response.data.success === true;
    } catch (error) {
      console.error("Backend test failed:", error);
      return false;
    }
  };

  const retryBackend = async () => {
    setIsLoading(true);
    setBackendError(null);

    const isBackendUp = await testBackendConnection();

    if (isBackendUp) {
      // Reload the page to fetch fresh data
      window.location.reload();
    } else {
      setBackendError("PHP backend is still not accessible");
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="edit-student-panel-screen">
        <div className="edit-student-dashboard-wrapper">
          <Sidebar />
          <div className="edit-student-container edit-student-loading-state">
            <div className="edit-student-loader-spinner edit-student-loader-large"></div>
            <p className="edit-student-loading-text">
              Loading student data from PHP backend...
            </p>
            {backendError && (
              <div className="edit-student-error-alert">
                <p>
                  <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                  {backendError}
                </p>
                <button
                  onClick={retryBackend}
                  className="edit-student-retry-btn"
                >
                  <FontAwesomeIcon icon={faSync} /> Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-student-panel-screen">
      <div className="edit-student-dashboard-wrapper">
        <Sidebar />
        <div className="edit-student-container">
          {/* Header Section */}
          <div className="edit-student-header">
            <div className="edit-student-header-left">
              <button onClick={handleBack} className="edit-student-back-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Students
              </button>
              <div className="edit-student-header-title">
                <h1 className="edit-student-main-title">
                  <FontAwesomeIcon icon={faUserEdit} />
                  Edit Student Profile
                </h1>
                <p className="edit-student-subtitle">
                  Update student information and academic details
                </p>
                {backendError && (
                  <div className="edit-student-header-error">
                    <FontAwesomeIcon icon={faWarning} />
                    <span>{backendError}</span>
                    <button
                      onClick={retryBackend}
                      className="edit-student-header-retry"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="edit-student-id-badge">
              <FontAwesomeIcon icon={faIdCard} />
              <span className="edit-student-id-text">
                {formData.student_id || "No ID"}
                {originalStudentId &&
                  formData.student_id !== originalStudentId && (
                    <span className="edit-student-id-changed">(Changed)</span>
                  )}
              </span>
              {isFamilyStudent && (
                <span className="edit-student-family-badge">
                  <FontAwesomeIcon icon={faUsers} />
                  Family Student
                </span>
              )}
            </div>
          </div>

          {/* Family Student Notice */}
          {isFamilyStudent && (
            <div className="edit-student-family-notice">
              <div className="edit-student-family-notice-header">
                <FontAwesomeIcon icon={faUsers} />
                <h4>Family Student Information</h4>
              </div>
              <p className="edit-student-family-notice-text">
                This student belongs to a family. Shared family information
                cannot be modified:
              </p>
              <div className="edit-student-family-fields-list">
                <span className="edit-student-family-field-item">
                  <FontAwesomeIcon icon={faLock} />
                  Father's Name
                </span>
                <span className="edit-student-family-field-item">
                  <FontAwesomeIcon icon={faLock} />
                  Mobile Number
                </span>
                <span className="edit-student-family-field-item">
                  <FontAwesomeIcon icon={faLock} />
                  B-Form/CNIC
                </span>
                <span className="edit-student-family-field-item">
                  <FontAwesomeIcon icon={faLock} />
                  Address
                </span>
              </div>
            </div>
          )}

          {/* Form Error Summary */}
          {Object.keys(formErrors).length > 0 && (
            <div className="edit-student-form-errors">
              <div className="edit-student-error-header">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <h4>Form Errors</h4>
              </div>
              <ul className="edit-student-error-list">
                {Object.entries(formErrors).map(
                  ([field, error]) =>
                    error && (
                      <li key={field} className="edit-student-error-item">
                        <strong>{field.replace(/_/g, " ")}:</strong> {error}
                      </li>
                    )
                )}
              </ul>
            </div>
          )}

          {/* Main Form */}
          <form
            className="edit-student-professional-form"
            onSubmit={handleSubmit}
          >
            {/* Personal Information Card */}
            <div className="edit-student-form-card edit-student-personal-card">
              <div className="edit-student-card-header">
                <FontAwesomeIcon icon={faUserCircle} />
                <h3 className="edit-student-card-title">
                  Personal Information
                  {isFamilyStudent && (
                    <span className="edit-student-card-subtitle">
                      (Shared fields disabled for family students)
                    </span>
                  )}
                </h3>
              </div>
              <div className="edit-student-card-body">
                <div className="edit-student-form-grid">
                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faUser} />
                      Student Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      required
                      className={`edit-student-form-input ${
                        formErrors.name ? "edit-student-input-error" : ""
                      }`}
                    />
                    {formErrors.name && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.name}
                      </div>
                    )}
                  </div>

                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faUser} />
                      Father's Name *
                    </label>
                    <input
                      type="text"
                      name="father_name"
                      value={formData.father_name}
                      onChange={handleInputChange}
                      placeholder="Enter father's name"
                      required
                      disabled={isFamilyStudent}
                      className={`edit-student-form-input ${
                        formErrors.father_name ? "edit-student-input-error" : ""
                      } ${
                        isFamilyStudent ? "edit-student-input-disabled" : ""
                      }`}
                    />
                    {formErrors.father_name && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.father_name}
                      </div>
                    )}
                    {isFamilyStudent && (
                      <small className="edit-student-field-hint">
                        Shared family field - cannot be modified
                      </small>
                    )}
                  </div>

                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faPhone} />
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      onBlur={handleMobileBlur}
                      placeholder="03XX-XXXXXXX"
                      required
                      disabled={isFamilyStudent}
                      className={`edit-student-form-input ${
                        formErrors.mobile ? "edit-student-input-error" : ""
                      } ${
                        isFamilyStudent ? "edit-student-input-disabled" : ""
                      }`}
                    />
                    <small className="edit-student-field-hint">
                      Auto-formats to: 03XX-XXXXXXX
                    </small>
                    {formErrors.mobile && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.mobile}
                      </div>
                    )}
                    {isFamilyStudent && (
                      <small className="edit-student-field-hint">
                        Shared family field - cannot be modified
                      </small>
                    )}
                  </div>

                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faIdCard} />
                      B-Form Number
                    </label>
                    <input
                      type="text"
                      name="b_form_no"
                      value={formData.b_form_no}
                      onChange={handleInputChange}
                      onBlur={handleBFormBlur}
                      placeholder="35202-1234567-1"
                      disabled={isFamilyStudent}
                      className={`edit-student-form-input ${
                        formErrors.b_form_no ? "edit-student-input-error" : ""
                      } ${
                        isFamilyStudent ? "edit-student-input-disabled" : ""
                      }`}
                    />
                    <small className="edit-student-field-hint">
                      Auto-formats to: 35202-1234567-1
                    </small>
                    {formErrors.b_form_no && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.b_form_no}
                      </div>
                    )}
                    {isFamilyStudent && (
                      <small className="edit-student-field-hint">
                        Shared family field - cannot be modified
                      </small>
                    )}
                  </div>

                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faVenusMars} />
                      Gender *
                    </label>
                    <div className="edit-student-radio-group">
                      {genders.map((gender) => (
                        <label
                          key={gender}
                          className="edit-student-radio-label"
                        >
                          <input
                            type="radio"
                            name="gender"
                            value={gender}
                            checked={formData.gender === gender}
                            onChange={handleInputChange}
                            required
                            className="edit-student-radio-input"
                          />
                          <span className="edit-student-radio-custom"></span>
                          <span className="edit-student-radio-text">
                            {gender}
                          </span>
                        </label>
                      ))}
                    </div>
                    {formErrors.gender && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.gender}
                      </div>
                    )}
                  </div>

                  <div className="edit-student-form-group edit-student-form-group-full-width">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faHome} />
                      Address *
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter complete address"
                      required
                      disabled={isFamilyStudent}
                      className={`edit-student-form-textarea ${
                        formErrors.address ? "edit-student-input-error" : ""
                      } ${
                        isFamilyStudent ? "edit-student-input-disabled" : ""
                      }`}
                      rows="3"
                    ></textarea>
                    {formErrors.address && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.address}
                      </div>
                    )}
                    {isFamilyStudent && (
                      <small className="edit-student-field-hint">
                        Shared family field - cannot be modified
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Academic Information Card */}
            <div className="edit-student-form-card edit-student-academic-card">
              <div className="edit-student-card-header">
                <FontAwesomeIcon icon={faGraduationCap} />
                <h3 className="edit-student-card-title">
                  Academic Information
                </h3>
              </div>
              <div className="edit-student-card-body">
                <div className="edit-student-form-grid">
                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faBuilding} />
                      Class *
                    </label>
                    <select
                      name="class"
                      value={formData.class}
                      onChange={handleInputChange}
                      required
                      className={`edit-student-form-select ${
                        formErrors.class ? "edit-student-input-error" : ""
                      }`}
                    >
                      <option value="">Select Class</option>
                      {classes.map((cls, index) => (
                        <option key={index} value={cls}>
                          {cls}
                        </option>
                      ))}
                    </select>
                    {formErrors.class && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.class}
                      </div>
                    )}
                  </div>

                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faBuilding} />
                      Section *
                    </label>
                    <select
                      name="section"
                      value={formData.section}
                      onChange={handleInputChange}
                      required
                      className={`edit-student-form-select ${
                        formErrors.section ? "edit-student-input-error" : ""
                      }`}
                      disabled={!formData.class}
                    >
                      <option value="">Select Section</option>
                      {sections.map((sec, index) => (
                        <option key={index} value={sec}>
                          {sec}
                        </option>
                      ))}
                    </select>
                    {!formData.class && (
                      <small className="edit-student-field-hint">
                        Select class first
                      </small>
                    )}
                    {formErrors.section && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.section}
                      </div>
                    )}
                  </div>

                  <div className="edit-student-form-group">
                    <label className="edit-student-form-label">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Family Status
                    </label>
                    <select
                      name="family_status"
                      value={formData.family_status}
                      onChange={handleInputChange}
                      className="edit-student-form-select"
                    >
                      {familyStatuses.map((status, index) => (
                        <option key={index} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    <small className="edit-student-field-hint">
                      Changing from Family to Solo will enable all fields
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* ID Generation Card */}
            <div className="edit-student-form-card edit-student-id-card">
              <div className="edit-student-card-header">
                <FontAwesomeIcon icon={faIdCard} />
                <h3 className="edit-student-card-title">
                  Student ID Management
                </h3>
              </div>
              <div className="edit-student-card-body">
                <div className="edit-student-id-control-panel">
                  <div className="edit-student-id-display">
                    <label className="edit-student-id-display-label">
                      Student ID
                    </label>
                    <div className="edit-student-id-input-group">
                      <input
                        type="text"
                        name="student_id"
                        value={formData.student_id}
                        onChange={handleInputChange}
                        className={`edit-student-id-display-input ${
                          formErrors.student_id
                            ? "edit-student-input-error"
                            : ""
                        }`}
                        placeholder="Enter or generate ID"
                      />
                      <div className="edit-student-id-status">
                        <FontAwesomeIcon
                          icon={
                            formData.student_id ? faCheckCircle : faInfoCircle
                          }
                        />
                        <span className="edit-student-id-status-text">
                          {formData.student_id ? "Assigned" : "Pending"}
                        </span>
                      </div>
                    </div>
                    <small className="edit-student-field-hint">
                      Format: STD-CLASS-XXX (e.g., STD-PG-25001)
                    </small>
                    {formErrors.student_id && (
                      <div className="edit-student-error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {formErrors.student_id}
                      </div>
                    )}
                  </div>

                  <div className="edit-student-id-actions">
                    <label className="edit-student-id-actions-label">
                      Auto-generate ID
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerateStudentID}
                      className="edit-student-generate-id-btn"
                      disabled={
                        !formData.class || !formData.section || isSaving
                      }
                    >
                      <FontAwesomeIcon icon={faSync} />
                      {isSaving ? "Generating..." : "Generate New ID"}
                    </button>
                    <small className="edit-student-field-hint">
                      Requires class and section selection
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="edit-student-form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="edit-student-btn-secondary"
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faUndo} />
                Reset Changes
              </button>
              <button
                type="submit"
                className="edit-student-btn-primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    Update Student
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Student Summary */}
          <div className="edit-student-summary">
            <h4 className="edit-student-summary-title">
              <FontAwesomeIcon icon={faInfoCircle} />
              Student Summary
            </h4>
            <div className="edit-student-summary-grid">
              <div className="edit-student-summary-item">
                <span className="edit-student-summary-label">Status:</span>
                <span
                  className={`edit-student-summary-value edit-student-status-${formData.family_status.toLowerCase()}`}
                >
                  {formData.family_status}
                  {isFamilyStudent && (
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="edit-student-summary-icon"
                    />
                  )}
                </span>
              </div>
              <div className="edit-student-summary-item">
                <span className="edit-student-summary-label">Class:</span>
                <span className="edit-student-summary-value">
                  {formData.class || "Not set"}
                </span>
              </div>
              <div className="edit-student-summary-item">
                <span className="edit-student-summary-label">Section:</span>
                <span className="edit-student-summary-value">
                  {formData.section || "Not set"}
                </span>
              </div>
              <div className="edit-student-summary-item">
                <span className="edit-student-summary-label">Original ID:</span>
                <span className="edit-student-summary-value">
                  {originalStudentId || "N/A"}
                </span>
              </div>
            </div>
            {isFamilyStudent && (
              <div className="edit-student-family-summary">
                <p className="edit-student-family-summary-text">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <strong>Note:</strong> This student is part of a family.
                  Shared family information cannot be modified here.
                </p>
              </div>
            )}
          </div>

          {/* Connection Status */}
          {/* <div className="edit-student-connection-status">
            <div className="edit-student-connection-status-item">
              <span className="edit-student-connection-label">
                PHP Backend:
              </span>
              <span className="edit-student-connection-status-indicator edit-student-connected">
                <FontAwesomeIcon icon={faCheckCircle} />
                Connected
              </span>
            </div>
            <div className="edit-student-connection-status-item">
              <span className="edit-student-connection-label">
                API Endpoint:
              </span>
              <span className="edit-student-connection-url">
                {API_BASE_URL}/studentCrudOP.php
              </span>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default EditStudentPanel;
